import React, { useState, useEffect } from 'react';
import { Modal, Button, Typography, Row, Col, Space, Radio, message, Spin, Alert, Select } from 'antd';
import {
    RocketOutlined,
    CheckOutlined,
    CloseOutlined,
    CheckCircleOutlined,
    CrownOutlined,
    CreditCardOutlined,
    LockOutlined,
} from '@ant-design/icons';
import { useAdminPath } from '../MarketerView/useScopedNavigate';
import { FormattedMessage, useIntl } from 'react-intl';
import { loadStripe, StripeElementsOptions } from '@stripe/stripe-js';
import {
    Elements,
    PaymentElement,
    useStripe,
    useElements,
} from '@stripe/react-stripe-js';
import { useAuth } from '../../hooks/useAuth';
import {
    useCreateSubscriptionMutation,
    useGetPaymentMethodsQuery,
    useGetAnnualPromotionQuery,
    useGetSignupConfigQuery,
    useSyncPaymentMethodsMutation,
    useSyncSubscriptionMutation,
} from '../../services/nestjs-base-api';
import { SubscriptionPlanEnum } from '../../services/nestjs-base-api/types';

const { Title, Text } = Typography;

// Initialize Stripe
const stripePromise =
    process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY &&
    process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY !== 'pk_test_your_stripe_publishable_key_here'
        ? loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY)
        : null;

interface UpgradeToProfessionalModalProps {
    visible: boolean;
    onClose: () => void;
    featureName?: string;
    onUpgrade?: () => void;
}

// Professional plan features - compact list
const PROFESSIONAL_FEATURES = [
    'Full Brand Equity Model Access',
    'Product Type Benchmarking',
    'AI Expert Insights',
    '20 AI Expert Credits/month',
    'Customer Segmentation',
    'Advanced Analytics',
];

// Inner component that uses Stripe hooks for new card entry
// This component uses PaymentElement in "payment" mode to collect card and handle 3DS in a single step
const NewCardPaymentForm: React.FC<{
    billingCycle: 'monthly' | 'yearly';
    billingCountry: string;
    onSuccess: () => void;
    onCancel: () => void;
    onProcessingChange?: (isProcessing: boolean) => void;
    organisationId: number;
    jwtToken: string;
    subscriptionClientSecret?: string; // Client secret from subscription's PaymentIntent
}> = ({ billingCycle, billingCountry, onSuccess, onCancel, onProcessingChange, organisationId, jwtToken, subscriptionClientSecret }) => {
    const intl = useIntl();
    const stripe = useStripe();
    const elements = useElements();
    const adminPath = useAdminPath();
    const [isProcessing, setIsProcessing] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [syncPaymentMethods] = useSyncPaymentMethodsMutation();
    const [syncSubscription] = useSyncSubscriptionMutation();

    const handleSubmit = async () => {
        if (!stripe || !elements) {
            return;
        }

        setIsProcessing(true);
        onProcessingChange?.(true);
        setErrorMessage(null);

        try {
            // Use confirmPayment with the subscription's PaymentIntent client secret
            // This handles card collection, 3DS authentication, and payment in a SINGLE step
            // No separate SetupIntent needed - avoiding double 3DS popup

            // First, submit the PaymentElement to validate and collect payment details
            // This is required before confirmPayment when using deferred payment flow
            const { error: submitError } = await elements.submit();
            if (submitError) {
                setErrorMessage(submitError.message || intl.formatMessage({
                    id: 'upgrade.cardError',
                    defaultMessage: 'Please check your card details and try again.'
                }));
                return;
            }

            const { error, paymentIntent } = await stripe.confirmPayment({
                elements,
                clientSecret: subscriptionClientSecret!,
                confirmParams: {
                    return_url: window.location.href,
                    payment_method_data: {
                        billing_details: {
                            address: {
                                country: billingCountry,
                            },
                        },
                    },
                },
                redirect: 'if_required',
            });

            if (error) {
                setErrorMessage(error.message || intl.formatMessage({
                    id: 'upgrade.paymentError',
                    defaultMessage: 'Payment failed. Please try again.'
                }));
                return;
            }

            if (paymentIntent?.status === 'succeeded') {
                message.success(intl.formatMessage({
                    id: 'upgrade.success',
                    defaultMessage: 'Successfully upgraded to Professional!'
                }));

                // Sync subscription status immediately - don't rely on webhook timing
                try {
                    await syncSubscription({ jwtToken, organisationId });
                } catch (syncErr) {
                    console.warn('Subscription sync failed:', syncErr);
                }

                // Sync payment methods to ensure the new card is saved locally
                try {
                    await syncPaymentMethods({ jwtToken, organisationId });
                } catch (syncErr) {
                    console.warn('Payment method sync failed:', syncErr);
                }

                onSuccess();
                // Redirect immediately - subscription is already synced
                window.location.href = adminPath('/dashboard/organisation?tab=billing&subtab=overview');
            } else if (paymentIntent?.status === 'processing') {
                message.info(intl.formatMessage({
                    id: 'upgrade.processing',
                    defaultMessage: 'Payment is processing. You will be notified when complete.'
                }));

                // Sync subscription status - may still be processing but update what we can
                try {
                    await syncSubscription({ jwtToken, organisationId });
                } catch (syncErr) {
                    console.warn('Subscription sync failed:', syncErr);
                }

                // Sync payment methods to ensure the new card is saved locally
                try {
                    await syncPaymentMethods({ jwtToken, organisationId });
                } catch (syncErr) {
                    console.warn('Payment method sync failed:', syncErr);
                }

                onSuccess();
                // Small delay to allow webhook to process subscription update
                setTimeout(() => {
                    window.location.href = adminPath('/dashboard/organisation?tab=billing&subtab=overview');
                }, 2000);
            } else {
                setErrorMessage(intl.formatMessage({
                    id: 'upgrade.paymentIncomplete',
                    defaultMessage: 'Payment was not completed. Please try again.'
                }));
            }
        } catch (err: any) {
            console.error('Subscription error:', err);
            setErrorMessage(err?.data?.message || err?.message || intl.formatMessage({
                id: 'upgrade.error',
                defaultMessage: 'Failed to upgrade. Please try again or contact support.'
            }));
        } finally {
            setIsProcessing(false);
            onProcessingChange?.(false);
        }
    };

    return (
        <div>
            <div style={{ marginBottom: 16 }}>
                <PaymentElement
                    options={{
                        layout: 'tabs',
                        fields: {
                            billingDetails: {
                                address: {
                                    country: 'never', // We collect country separately for tax calculation
                                },
                            },
                        },
                    }}
                />
            </div>

            {errorMessage && (
                <Alert
                    message={errorMessage}
                    type="error"
                    showIcon
                    style={{ marginBottom: 16 }}
                />
            )}

            <div style={{ marginTop: 8, marginBottom: 16 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                    <LockOutlined style={{ marginRight: 4 }} />
                    <FormattedMessage
                        id="upgrade.stripeSecure"
                        defaultMessage="By providing your card information, you allow BrandScience to charge your card for future payments in accordance with their terms."
                    />
                </Text>
            </div>

            <div style={{ display: 'flex', justifyContent: 'center', gap: 12 }}>
                <Button size="large" onClick={onCancel} disabled={isProcessing}>
                    <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
                </Button>
                <Button
                    type="primary"
                    size="large"
                    icon={<LockOutlined />}
                    onClick={handleSubmit}
                    loading={isProcessing}
                    disabled={!stripe || !elements}
                    style={{
                        background: 'linear-gradient(135deg, #F59E0B 0%, #FBBF24 100%)',
                        border: 'none',
                        height: 44,
                        paddingLeft: 24,
                        paddingRight: 24,
                        fontWeight: 600,
                    }}
                >
                    <FormattedMessage id="upgrade.payAndUpgrade" defaultMessage="Pay & Upgrade" />
                </Button>
            </div>

            <div style={{ marginTop: 16, textAlign: 'center' }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                    <LockOutlined style={{ marginRight: 4 }} />
                    <FormattedMessage
                        id="upgrade.stripeSecurePayment"
                        defaultMessage="Payments are securely processed by Stripe"
                    />
                </Text>
            </div>
        </div>
    );
};

// Helper to get browser's country from locale
function getBrowserCountry(): string {
    try {
        const locale = navigator.language || (navigator as any).userLanguage;
        if (locale && locale.includes('-')) {
            return locale.split('-')[1].toUpperCase();
        }
        // Common locale to country mappings
        const localeCountryMap: Record<string, string> = {
            'en': 'US',
            'de': 'DE',
            'fr': 'FR',
            'es': 'ES',
            'it': 'IT',
            'pt': 'BR',
            'ja': 'JP',
            'ko': 'KR',
            'zh': 'CN',
        };
        const langCode = locale?.split('-')[0]?.toLowerCase();
        return localeCountryMap[langCode] || 'US';
    } catch {
        return 'US';
    }
}

export const UpgradeToProfessionalModal: React.FC<UpgradeToProfessionalModalProps> = ({
    visible,
    onClose,
    featureName,
    onUpgrade,
}) => {
    const adminPath = useAdminPath();
    const intl = useIntl();
    const { user } = useAuth();
    const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly' | null>(null);
    const [billingCountry, setBillingCountry] = useState<string>(getBrowserCountry());
    const [isProcessing, setIsProcessing] = useState(false);
    const [isNewCardProcessing, setIsNewCardProcessing] = useState(false);
    const [showCheckout, setShowCheckout] = useState(false);
    const [subscriptionClientSecret, setSubscriptionClientSecret] = useState<string | null>(null);
    const [paymentInitError, setPaymentInitError] = useState<string | null>(null);
    const [isCreatingSubscription, setIsCreatingSubscription] = useState(false);

    const organisationId = user?.organisation?.id;
    const jwtToken = user?.jwtToken;

    const [createSubscription] = useCreateSubscriptionMutation();
    const [syncSubscription] = useSyncSubscriptionMutation();

    const { data: paymentMethods, isLoading: paymentMethodsLoading, refetch: refetchPaymentMethods } = useGetPaymentMethodsQuery(
        { jwtToken: jwtToken!, organisationId: organisationId! },
        { skip: !jwtToken || !organisationId || !showCheckout }
    );

    const { data: annualPromotion } = useGetAnnualPromotionQuery(
        { jwtToken: jwtToken! },
        { skip: !jwtToken || !visible }
    );

    // SYR-109: with no trial on offer, the 60-day monthly-to-annual promise is
    // part of the monthly option's pitch. Strict === false so the line only
    // appears when the API explicitly says the trial is off.
    const { data: signupConfig } = useGetSignupConfigQuery();
    const trialOff = signupConfig?.freeTrialEnabled === false;

    const defaultPaymentMethod = paymentMethods?.find(pm => pm.isDefault) || paymentMethods?.[0];
    const hasPaymentMethod = !!defaultPaymentMethod;

    // Track the billing cycle and country that were used to create the current subscription
    const [subscriptionBillingCycle, setSubscriptionBillingCycle] = useState<'monthly' | 'yearly' | null>(null);
    const [subscriptionBillingCountry, setSubscriptionBillingCountry] = useState<string | null>(null);

    // Reset subscription when billing cycle or country changes (price/tax may change)
    useEffect(() => {
        if (subscriptionClientSecret) {
            const cycleChanged = subscriptionBillingCycle && billingCycle !== subscriptionBillingCycle;
            const countryChanged = subscriptionBillingCountry && billingCountry !== subscriptionBillingCountry;
            if (cycleChanged || countryChanged) {
                setSubscriptionClientSecret(null);
                setSubscriptionBillingCycle(null);
                setSubscriptionBillingCountry(null);
                // Note: The incomplete subscription in Stripe will be cleaned up automatically
                // or by Stripe's webhook handling. We create a new one with the correct settings.
            }
        }
    }, [billingCycle, billingCountry, subscriptionBillingCycle, subscriptionBillingCountry, subscriptionClientSecret]);

    // Create subscription upfront when showing checkout and no saved payment method
    // This gives us the PaymentIntent's clientSecret for the PaymentElement to use
    // Single 3DS flow: card collection + 3DS auth + payment all in one step
    // Only create after user has selected a billing cycle to avoid orphaned subscriptions
    useEffect(() => {
        const initSubscription = async () => {
            if (showCheckout && billingCycle && !hasPaymentMethod && !subscriptionClientSecret && !isCreatingSubscription && !paymentInitError && jwtToken && organisationId) {
                setIsCreatingSubscription(true);
                try {
                    const result = await createSubscription({
                        jwtToken,
                        organisationId,
                        payload: {
                            brandId: 0,
                            plan: SubscriptionPlanEnum.Professional,
                            billingCycle,
                            // No paymentMethodId - we'll collect card via PaymentElement
                            billingCountry,
                        }
                    }).unwrap();

                    if (result?.clientSecret) {
                        setSubscriptionClientSecret(result.clientSecret);
                        setSubscriptionBillingCycle(billingCycle);
                        setSubscriptionBillingCountry(billingCountry);
                        setPaymentInitError(null);
                    } else {
                        // Subscription created without needing payment (e.g., trial or $0 invoice)
                        setPaymentInitError('Unable to initialize payment. The subscription may already be active.');
                    }
                } catch (err: any) {
                    setPaymentInitError(err?.data?.message || 'Failed to initialize payment');
                } finally {
                    setIsCreatingSubscription(false);
                }
            }
        };
        initSubscription();
    }, [showCheckout, hasPaymentMethod, subscriptionClientSecret, isCreatingSubscription, paymentInitError, jwtToken, organisationId, billingCycle, billingCountry, createSubscription]);

    // Calculate prices with potential discount (pricing comes from system settings via API)
    const hasAnnualDiscount = annualPromotion?.active && annualPromotion.discountPercent > 0;
    const couponDiscountPercent = annualPromotion?.discountPercent || 0;
    const monthlyPrice = annualPromotion?.pricing?.monthlyPrice || 990;
    const yearlyOriginalPrice = annualPromotion?.pricing?.yearlyPrice || 9900;
    const yearlyDiscountedPrice = hasAnnualDiscount
        ? Math.round(yearlyOriginalPrice * (1 - couponDiscountPercent / 100))
        : yearlyOriginalPrice;

    // Calculate actual savings vs paying monthly for a year
    const yearlyEquivalentOfMonthly = monthlyPrice * 12;
    const actualSavingsAmount = yearlyEquivalentOfMonthly - yearlyDiscountedPrice;
    const actualSavingsPercent = Math.round((actualSavingsAmount / yearlyEquivalentOfMonthly) * 100);

    // Standard yearly savings (without promotional coupon)
    const standardYearlySavings = yearlyEquivalentOfMonthly - yearlyOriginalPrice;

    const handleUpgrade = async () => {
        if (!showCheckout) {
            setShowCheckout(true);
            return;
        }

        // Require billing cycle selection
        if (!billingCycle) {
            return;
        }

        // If no payment method, the inline form handles the upgrade
        if (!defaultPaymentMethod) {
            return;
        }

        // Process upgrade with existing payment method
        setIsProcessing(true);
        try {
            const subscriptionResult = await createSubscription({
                jwtToken: jwtToken!,
                organisationId: organisationId!,
                payload: {
                    brandId: 0,
                    plan: SubscriptionPlanEnum.Professional,
                    billingCycle,
                    paymentMethodId: defaultPaymentMethod.stripePaymentMethodId,
                    billingCountry,
                }
            }).unwrap();

            // Check if 3D Secure authentication is required
            if (subscriptionResult?.clientSecret) {
                const stripe = await stripePromise;
                if (!stripe) {
                    throw new Error('Stripe not initialized');
                }

                const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(
                    subscriptionResult.clientSecret
                );

                if (confirmError) {
                    message.error(confirmError.message || intl.formatMessage({
                        id: 'upgrade.3dsError',
                        defaultMessage: 'Payment authentication failed. Please try again.'
                    }));
                    setIsProcessing(false);
                    return;
                }

                if (paymentIntent?.status !== 'succeeded') {
                    message.error(intl.formatMessage({
                        id: 'upgrade.paymentIncomplete',
                        defaultMessage: 'Payment was not completed. Please try again.'
                    }));
                    setIsProcessing(false);
                    return;
                }

                // Sync subscription status immediately after 3DS success
                try {
                    await syncSubscription({ jwtToken: jwtToken!, organisationId: organisationId! });
                } catch (syncErr) {
                    console.warn('Subscription sync failed:', syncErr);
                }
            } else {
                // No 3DS required - subscription was created with active status
                // Sync to ensure local DB has correct status
                try {
                    await syncSubscription({ jwtToken: jwtToken!, organisationId: organisationId! });
                } catch (syncErr) {
                    console.warn('Subscription sync failed:', syncErr);
                }
            }

            message.success(intl.formatMessage({
                id: 'upgrade.success',
                defaultMessage: 'Successfully upgraded to Professional!'
            }));
            // Don't reset isProcessing on success - we're redirecting and don't want button to flash
            onClose();
            window.location.href = adminPath('/dashboard/organisation?tab=billing&subtab=overview');
        } catch (error: any) {
            message.error(error?.data?.message || intl.formatMessage({
                id: 'upgrade.error',
                defaultMessage: 'Failed to upgrade. Please try again or contact support.'
            }));
            // Only reset isProcessing on error so user can retry
            setIsProcessing(false);
        }
    };

    const handleClose = () => {
        setShowCheckout(false);
        setBillingCycle(null);
        setSubscriptionClientSecret(null);
        setSubscriptionBillingCycle(null);
        setSubscriptionBillingCountry(null);
        setPaymentInitError(null);
        setIsNewCardProcessing(false);
        setIsCreatingSubscription(false);
        onClose();
    };

    const handleCardSuccess = () => {
        refetchPaymentMethods();
    };

    const elementsOptions: StripeElementsOptions = {
        clientSecret: subscriptionClientSecret || undefined,
        appearance: {
            theme: 'stripe',
            variables: {
                colorPrimary: '#F59E0B',
            },
        },
    };

    const isStripeConfigured = stripePromise !== null;

    return (
        <Modal
            open={visible}
            onCancel={handleClose}
            footer={null}
            width={700}
            centered
            closable={false}
            styles={{
                body: { padding: 0 },
                content: { overflow: 'hidden' },
            }}
        >
            {/* Header */}
            <div style={{
                background: 'linear-gradient(135deg, #F59E0B 0%, #FBBF24 100%)',
                padding: '24px 32px',
                borderRadius: '8px 8px 0 0',
                position: 'relative',
            }}>
                {/* Close Button */}
                <Button
                    type="text"
                    onClick={handleClose}
                    style={{
                        position: 'absolute',
                        top: hasAnnualDiscount ? 4 : 12,
                        right: 3,
                        width: 32,
                        height: 32,
                        padding: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'rgba(255,255,255,0.15)',
                        borderRadius: 8,
                        border: 'none',
                        zIndex: 10,
                    }}
                    icon={<CloseOutlined style={{ color: 'white', fontSize: 14 }} />}
                />

                {/* Limited Time Banner */}
                {hasAnnualDiscount && (
                    <div style={{
                        background: 'rgba(82, 196, 26, 0.9)',
                        margin: '-24px -32px 20px -32px',
                        padding: '8px 32px',
                        textAlign: 'center',
                    }}>
                        <Text style={{ color: 'white', fontWeight: 600, fontSize: 14 }}>
                            🎉 Limited Time Offer: Save {actualSavingsPercent}% on annual billing!
                        </Text>
                    </div>
                )}

                <Row align="middle" justify="space-between" gutter={[24, 16]}>
                    <Col xs={24} sm={14}>
                        <Space align="start">
                            <div style={{
                                width: 48,
                                height: 48,
                                borderRadius: 12,
                                background: 'rgba(255,255,255,0.2)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}>
                                <RocketOutlined style={{ fontSize: 24, color: 'white' }} />
                            </div>
                            <div>
                                <Title level={3} style={{ margin: 0, color: 'white' }}>
                                    <FormattedMessage id="upgrade.title" defaultMessage="Upgrade to Professional" />
                                </Title>
                                <Text style={{ color: 'rgba(255,255,255,0.85)' }}>
                                    {featureName
                                        ? <FormattedMessage id="upgrade.unlockFeature" defaultMessage="Unlock {featureName} and more" values={{ featureName }} />
                                        : <FormattedMessage id="upgrade.subtitle" defaultMessage="Unlock the full power of brand equity insights" />}
                                </Text>
                            </div>
                        </Space>
                    </Col>
                    <Col xs={24} sm={10}>
                        <div style={{
                            textAlign: 'right',
                            background: 'rgba(255,255,255,0.1)',
                            borderRadius: 12,
                            padding: '16px 20px',
                        }}>
                            <div>
                                <Text style={{ color: 'white', fontSize: 32, fontWeight: 700 }}>
                                    ${monthlyPrice.toLocaleString()}
                                </Text>
                                <Text style={{ color: 'rgba(255,255,255,0.85)', fontSize: 14 }}> USD/mo</Text>
                            </div>
                            <Text style={{ color: 'rgba(255,255,255,0.6)', fontSize: 11, display: 'block' }}>
                                <FormattedMessage id="upgrade.plusTax" defaultMessage="plus applicable taxes" />
                            </Text>
                            <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid rgba(255,255,255,0.2)' }}>
                                {hasAnnualDiscount ? (
                                    <div>
                                        <Text style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13, textDecoration: 'line-through' }}>
                                            ${yearlyOriginalPrice.toLocaleString()}/yr
                                        </Text>
                                        <Text style={{ color: '#52c41a', fontSize: 14, fontWeight: 600, marginLeft: 8 }}>
                                            ${yearlyDiscountedPrice.toLocaleString()}/yr
                                        </Text>
                                        <div>
                                            <Text style={{ color: 'rgba(255,255,255,0.7)', fontSize: 11 }}>
                                                First year only
                                            </Text>
                                        </div>
                                    </div>
                                ) : (
                                    <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: 13 }}>
                                        or ${yearlyOriginalPrice.toLocaleString()}/yr (save ${standardYearlySavings.toLocaleString()})
                                    </Text>
                                )}
                            </div>
                        </div>
                    </Col>
                </Row>
            </div>

            {/* Content */}
            <div style={{ padding: '24px 32px' }}>
                {showCheckout ? (
                    /* Checkout Step */
                    <div>
                        <Title level={5} style={{ marginBottom: 20 }}>
                            <FormattedMessage id="upgrade.selectBillingCycle" defaultMessage="Select Billing Cycle" />
                            {!billingCycle && (
                                <Text type="secondary" style={{ fontWeight: 'normal', fontSize: 14, marginLeft: 8 }}>
                                    (please select one)
                                </Text>
                            )}
                        </Title>
                        <Radio.Group
                            value={billingCycle}
                            onChange={(e) => {
                                setBillingCycle(e.target.value);
                                // A failed init must not outlive the inputs that caused it —
                                // without this, one tax-calculation 400 latches the "coming
                                // soon" alert and no later choice ever retries.
                                setPaymentInitError(null);
                            }}
                            style={{ width: '100%', marginBottom: 24 }}
                        >
                            <Space direction="vertical" style={{ width: '100%' }}>
                                <Radio.Button
                                    value="monthly"
                                    style={{
                                        width: '100%',
                                        height: 'auto',
                                        padding: '16px 20px',
                                        borderRadius: 8,
                                    }}
                                >
                                    <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                                        <div>
                                            <Text strong><FormattedMessage id="upgrade.monthly" defaultMessage="Monthly" /></Text>
                                            <br />
                                            <Text type="secondary" style={{ fontSize: 12 }}>
                                                <FormattedMessage id="upgrade.monthlyDesc" defaultMessage="Billed monthly" />
                                            </Text>
                                            {trialOff && (
                                                <>
                                                    <br />
                                                    <Text style={{ fontSize: 12, color: '#52c41a' }}>
                                                        <FormattedMessage
                                                            id="upgrade.monthlyAnnualSwapNote"
                                                            defaultMessage="Start monthly if you like. Any time in your first 60 days, ask us to move you to annual and everything you've already paid counts toward it."
                                                        />
                                                    </Text>
                                                </>
                                            )}
                                        </div>
                                        <div style={{ textAlign: 'right' }}>
                                            <Text strong style={{ fontSize: 18 }}>${monthlyPrice.toLocaleString()} <Text type="secondary">USD/mo</Text></Text>
                                        </div>
                                    </div>
                                </Radio.Button>
                                <Radio.Button
                                    value="yearly"
                                    style={{
                                        width: '100%',
                                        height: 'auto',
                                        padding: '16px 20px',
                                        borderRadius: 8,
                                    }}
                                >
                                    <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                                        <div>
                                            <Text strong><FormattedMessage id="upgrade.yearly" defaultMessage="Yearly" /></Text>
                                            <Text style={{ marginLeft: 8, color: '#52c41a', fontSize: 12 }}>
                                                {hasAnnualDiscount
                                                    ? <>{actualSavingsPercent}% OFF - Limited Time!</>
                                                    : <FormattedMessage
                                                        id="upgrade.saveAmount"
                                                        defaultMessage="Save ${savingsAmount} USD"
                                                        values={{ savingsAmount: standardYearlySavings.toLocaleString() }}
                                                      />
                                                }
                                            </Text>
                                            <br />
                                            <Text type="secondary" style={{ fontSize: 12 }}>
                                                {hasAnnualDiscount
                                                    ? `First year only, then $${yearlyOriginalPrice.toLocaleString()} USD/yr`
                                                    : <FormattedMessage id="upgrade.yearlyDesc" defaultMessage="Billed annually" />
                                                }
                                            </Text>
                                        </div>
                                        <div style={{ textAlign: 'right' }}>
                                            {hasAnnualDiscount && (
                                                <>
                                                    <Text style={{ textDecoration: 'line-through', color: '#999', fontSize: 14 }}>
                                                        ${yearlyOriginalPrice.toLocaleString()}
                                                    </Text>
                                                    <br />
                                                </>
                                            )}
                                            <Text strong style={{ fontSize: 18 }}>
                                                ${yearlyDiscountedPrice.toLocaleString()} <Text type="secondary">USD/yr</Text>
                                            </Text>
                                        </div>
                                    </div>
                                </Radio.Button>
                            </Space>
                        </Radio.Group>
                        <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: -16, marginBottom: 16 }}>
                            <FormattedMessage id="upgrade.plusTaxNote" defaultMessage="Prices exclude applicable taxes (e.g. GST for Australian customers)" />
                        </Text>

                        {/* Billing Country - only show after billing cycle is selected */}
                        {billingCycle && (
                            <div style={{ marginBottom: 24 }}>
                                <Text strong style={{ display: 'block', marginBottom: 8 }}>
                                    <FormattedMessage id="upgrade.billingCountry" defaultMessage="Billing Country" />
                                </Text>
                                <Select
                                    value={billingCountry}
                                    onChange={(value) => {
                                        setBillingCountry(value);
                                        // The useEffect will detect the country mismatch and reset the subscription
                                        setPaymentInitError(null);
                                    }}
                                    style={{ width: '100%' }}
                                    showSearch
                                    optionFilterProp="label"
                                    options={[
                                        { value: 'AU', label: 'Australia' },
                                        { value: 'US', label: 'United States' },
                                        { value: 'GB', label: 'United Kingdom' },
                                        { value: 'CA', label: 'Canada' },
                                        { value: 'NZ', label: 'New Zealand' },
                                        { value: 'DE', label: 'Germany' },
                                        { value: 'FR', label: 'France' },
                                        { value: 'JP', label: 'Japan' },
                                        { value: 'SG', label: 'Singapore' },
                                        { value: 'IN', label: 'India' },
                                        { value: 'BR', label: 'Brazil' },
                                        { value: 'MX', label: 'Mexico' },
                                        { value: 'ES', label: 'Spain' },
                                        { value: 'IT', label: 'Italy' },
                                        { value: 'NL', label: 'Netherlands' },
                                        { value: 'SE', label: 'Sweden' },
                                        { value: 'CH', label: 'Switzerland' },
                                        { value: 'AT', label: 'Austria' },
                                        { value: 'BE', label: 'Belgium' },
                                        { value: 'DK', label: 'Denmark' },
                                        { value: 'FI', label: 'Finland' },
                                        { value: 'IE', label: 'Ireland' },
                                        { value: 'NO', label: 'Norway' },
                                        { value: 'PL', label: 'Poland' },
                                        { value: 'PT', label: 'Portugal' },
                                        { value: 'ZA', label: 'South Africa' },
                                        { value: 'KR', label: 'South Korea' },
                                        { value: 'AE', label: 'United Arab Emirates' },
                                        { value: 'HK', label: 'Hong Kong' },
                                        { value: 'MY', label: 'Malaysia' },
                                        { value: 'PH', label: 'Philippines' },
                                        { value: 'TH', label: 'Thailand' },
                                        { value: 'ID', label: 'Indonesia' },
                                        { value: 'VN', label: 'Vietnam' },
                                    ].sort((a, b) => a.label.localeCompare(b.label))}
                                />
                                <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 4 }}>
                                    <FormattedMessage id="upgrade.billingCountryNote" defaultMessage="Used for tax calculation" />
                                </Text>
                            </div>
                        )}

                        {/* Payment Method */}
                        <Title level={5} style={{ marginBottom: 16 }}>
                            <FormattedMessage id="upgrade.paymentMethod" defaultMessage="Payment Method" />
                        </Title>
                        {!billingCycle ? (
                            <div style={{
                                textAlign: 'center',
                                padding: '24px 20px',
                                background: '#fafafa',
                                borderRadius: 8,
                                border: '1px dashed #d9d9d9',
                            }}>
                                <CreditCardOutlined style={{ fontSize: 32, color: '#d9d9d9', marginBottom: 8 }} />
                                <div>
                                    <Text type="secondary">
                                        <FormattedMessage id="upgrade.selectCycleFirst" defaultMessage="Please select a billing cycle above to continue" />
                                    </Text>
                                </div>
                            </div>
                        ) : paymentMethodsLoading || isCreatingSubscription ? (
                            <div style={{ textAlign: 'center', padding: 20 }}>
                                <Spin />
                                <div style={{ marginTop: 8 }}>
                                    <Text type="secondary">
                                        {isCreatingSubscription
                                            ? <FormattedMessage id="upgrade.preparingPayment" defaultMessage="Preparing payment..." />
                                            : <FormattedMessage id="upgrade.loading" defaultMessage="Loading..." />
                                        }
                                    </Text>
                                </div>
                            </div>
                        ) : defaultPaymentMethod ? (
                            /* Show existing payment method */
                            <div style={{
                                padding: '16px 20px',
                                background: '#f6ffed',
                                border: '1px solid #b7eb8f',
                                borderRadius: 8,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 12,
                            }}>
                                <CreditCardOutlined style={{ fontSize: 24, color: '#52c41a' }} />
                                <div>
                                    <Text strong style={{ textTransform: 'capitalize' }}>
                                        {defaultPaymentMethod.cardBrand} •••• {defaultPaymentMethod.cardLast4}
                                    </Text>
                                    <br />
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                        <FormattedMessage
                                            id="upgrade.expires"
                                            defaultMessage="Expires {month}/{year}"
                                            values={{
                                                month: String(defaultPaymentMethod.cardExpMonth).padStart(2, '0'),
                                                year: String(defaultPaymentMethod.cardExpYear).slice(-2)
                                            }}
                                        />
                                    </Text>
                                </div>
                            </div>
                        ) : !isStripeConfigured ? (
                            <Alert
                                type="error"
                                showIcon
                                message={<FormattedMessage id="upgrade.stripeNotConfigured" defaultMessage="Payment system not configured" />}
                                description={<FormattedMessage id="upgrade.stripeNotConfiguredDesc" defaultMessage="Please contact support to complete your upgrade." />}
                            />
                        ) : paymentInitError ? (
                            <Alert
                                type="warning"
                                showIcon
                                message={<FormattedMessage id="upgrade.paymentInitError" defaultMessage="Subscriptions are coming soon!" />}
                                description={<FormattedMessage id="upgrade.paymentInitErrorDesc" defaultMessage="We're still setting up our payment system. In the meantime, you can continue enjoying your free trial. We'll let you know as soon as subscriptions are available!" />}
                            />
                        ) : subscriptionClientSecret ? (
                            /* Show inline card form - uses PaymentIntent mode for single 3DS flow */
                            <Elements stripe={stripePromise} options={elementsOptions}>
                                <NewCardPaymentForm
                                    billingCycle={billingCycle as 'monthly' | 'yearly'}
                                    billingCountry={billingCountry}
                                    onSuccess={handleCardSuccess}
                                    onCancel={handleClose}
                                    onProcessingChange={setIsNewCardProcessing}
                                    organisationId={organisationId!}
                                    jwtToken={jwtToken!}
                                    subscriptionClientSecret={subscriptionClientSecret}
                                />
                            </Elements>
                        ) : (
                            <div style={{ textAlign: 'center', padding: 20 }}>
                                <Spin />
                                <div style={{ marginTop: 8 }}>
                                    <Text type="secondary">
                                        <FormattedMessage id="upgrade.loadingPayment" defaultMessage="Loading payment form..." />
                                    </Text>
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                    /* Features Overview Step - Compact */
                    <div style={{
                        background: '#f6ffed',
                        border: '1px solid #b7eb8f',
                        borderRadius: 8,
                        padding: '16px 20px',
                    }}>
                        <Title level={5} style={{ marginBottom: 12, marginTop: 0, color: '#52c41a' }}>
                            <CheckCircleOutlined style={{ marginRight: 8 }} />
                            <FormattedMessage id="upgrade.includedInProfessional" defaultMessage="Included in Professional:" />
                        </Title>
                        <Row gutter={[8, 8]}>
                            {PROFESSIONAL_FEATURES.map((feature, index) => (
                                <Col xs={12} key={index}>
                                    <Space size={6}>
                                        <CheckOutlined style={{ color: '#52c41a', fontSize: 12 }} />
                                        <Text style={{ fontSize: 13 }}>{feature}</Text>
                                    </Space>
                                </Col>
                            ))}
                        </Row>
                    </div>
                )}
            </div>

            {/* Footer CTA - only show when we have a payment method or not in checkout, and not processing new card */}
            {(!showCheckout || hasPaymentMethod) && !isNewCardProcessing && (
                <div style={{
                    background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
                    padding: '20px 32px',
                    borderTop: '1px solid #fde68a',
                    borderRadius: '0 0 8px 8px',
                    textAlign: 'center',
                }}>
                    <Text type="secondary" style={{ fontSize: 13, display: 'block', marginBottom: 16 }}>
                        <FormattedMessage id="upgrade.terms" defaultMessage="All prices in USD. No setup fees." />
                    </Text>
                    <Space>
                        <Button size="large" onClick={handleClose} disabled={isProcessing}>
                            {showCheckout
                                ? <FormattedMessage id="upgrade.back" defaultMessage="Back" />
                                : <FormattedMessage id="upgrade.maybeLater" defaultMessage="Maybe Later" />
                            }
                        </Button>
                        <Button
                            type="primary"
                            size="large"
                            icon={<CrownOutlined />}
                            onClick={handleUpgrade}
                            loading={isProcessing}
                            disabled={showCheckout && !billingCycle}
                            style={{
                                background: 'linear-gradient(135deg, #F59E0B 0%, #FBBF24 100%)',
                                border: 'none',
                                height: 44,
                                paddingLeft: 24,
                                paddingRight: 24,
                                fontWeight: 600,
                            }}
                        >
                            {showCheckout
                                ? <FormattedMessage id="upgrade.confirmUpgrade" defaultMessage="Confirm Upgrade" />
                                : <FormattedMessage id="upgrade.upgradeNow" defaultMessage="Upgrade Now" />
                            }
                        </Button>
                    </Space>
                </div>
            )}
        </Modal>
    );
};

export default UpgradeToProfessionalModal;
